<template>
    <LoadingDialog :visible="actionIsBeingExecuted" />

    <ConfirmationModal 
      :title="currentConfirmationData.title"
      :text="currentConfirmationData.text"
      :visible="confirmationModalVisibility"
      @emitResponse="confirmationResponse"
    />
    <LoaderComponent class="col-span-5 text-center pt-5 mt-5" v-if="tabLoading" />
    <div class="content-wrapper col-span-5 md:col-span-3 xl:col-span-2 flex flex-col gap-y-5" v-if="!tabLoading">
        <section class="w-full p-4 md:p-6 bg-white shadow-md rounded-md border border-iron">
            <h1 class="mb-6 text-lg font-semibold">{{ translate('certifications', 'ucfirst') }}</h1>
            <form
                class="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-6 text-base font-medium"
            >
                <span class="col-span-3 p-float-label">
                    <AutoComplete
                        inputId="certificationAutocomplete"
                        v-model="certificationName" 
                        :suggestions="certificationList" 
                        optionLabel="name"
                        @complete="searchForCertification"
                        :placeholder="`${translate('type to search')}...`"
                        :emptyMessage="translate('no results found', 'ucfirst')"
                        :emptySearchMessage="translate('no results found', 'ucfirst')"
                        :inputClass="'w-full'"
                        multiple
                        fluid 
                        class="w-full rounded border border-iron autocomplete-w-full"
                    />
                </span> 
                <hr class="iron pt-2 pb-2 col-span-3">
                <div class="col-span-3 flex flex-wrap">
                    <div v-for="(certification, index) of certificationName" :class="`w-full mb-3 md:pe-3 md:w-1/3 certification-opt-${certification.code}-${certification.name.replaceAll(' ', 'aaaa-aaaa-aaaa')}`" :key="index">
                        <div class="rounded-full" style="border: 1px solid #cbd5e1;padding: 0.5rem 0.75rem!important; font-size: 1rem;">{{ certification.name }}</div>
                    </div>
                </div>
            </form>
        </section>
    </div>
    <ActionCard 
        v-if="!tabLoading"
        :jobId="jobId"
        :jobStatus="props.jobStatus"
        :actionButtonText="translate('save job certifications', 'ucfirst')"
        @emitClick="openConfirmationModal('certificationSave')"
        class="col-span-3 md:col-span-2 xl:col-span-1 flex flex-col gap-y-5 sticky top-24 h-[calc(100vh-theme(spacing.20))]"
    />
</template>

<script setup>
    import { ref, onMounted, watch, reactive, onBeforeMount } from "vue";
    import requestHandler from "@/composables/requestHandler";
    import AutoComplete from "primevue/autocomplete";
    import { translate } from "@/composables/translate";
    import { sleep } from "@/composables/sleep";
    import store from "@/store";
    import utils from "@/composables/utils";
    import ActionCard from "@/components/Dashboard/Company/SaveJob/ActionCard"
    import ConfirmationModal from "@/components/Utils/ConfirmationModal";
    import jobData from "@/composables/jobData";
    import LoadingDialog from "@/components/Utils/LoadingDialog.vue";
    import { showToast } from "@/composables/toast";
    import { useToast } from "primevue/usetoast";
    import LoaderComponent from "@/components/Utils/LoaderComponent.vue";

    let toast = useToast();

    const props = defineProps({
        jobId: {
            type: String,
            required: false,
            default: null
        },
        jobStatus: {
            type: String,
            required: false,
            default: 'draft'
        }
    });

    let tabLoading = ref(true);
    let actionIsBeingExecuted = ref(false);
    let jobId = ref(null);
    onMounted(function(){
        handleAutoCompleteElements();
        getEssentialData();
    });

    onBeforeMount(function(){
        jobId.value = props.jobId;
        if(jobId.value == null)
            goToJobPage();
    });

    async function getEssentialData(){
        tabLoading.value = true;
        await fetchAndFeedCertifications();
        tabLoading.value = false;
    }

    async function fetchAndFeedCertifications(){
        if(!jobId.value)
            return;
        let values = await jobData.searchJobCertifications(jobId.value);
        certificationName.value = values.certification;
    }

    async function goToJobPage(){
        await requestHandler.redirect(`/dashboard/company/${store.getters.getUserSlug}/post-job`);
    }

    watch(() => props.jobId, (newValue) => {
      jobId.value = newValue;
      handleAutoCompleteElements();
    });

    
    let confirmationModalVisibility = ref(false);
    const confirmationData = {
        certificationSearch: {
            title: translate('no match found for this result', 'ucfirst'),
            text: translate('do you wish to add it anyway to your job certifications? This action will make this certification be avaliable to professionals only after its validation', 'ucfirst')
        },
        certificationSave: {
            title: translate('are you sure?', 'ucfirst'),
            text: translate('do you wish to save these job certifications? This action will make your job unavaliable until it passes by our validation trials', 'ucfirst')
        }
    }
    let currentConfirmationData = reactive({
        title: '',
        text: '',
        action: ''
    })
    function openConfirmationModal(action = ''){
        currentConfirmationData.action = '';
        confirmationModalVisibility.value = false;
        if(action == '' || !['certificationSearch','certificationSave'].includes(action))
            return;
        currentConfirmationData.action = action;
        currentConfirmationData.title = confirmationData[action].title;
        currentConfirmationData.text  = confirmationData[action].text;
        confirmationModalVisibility.value = true;
    }

    let termToSearch = ref('');
    let customCertifications = ref([]);
    let certificationName = ref([]);
    let certificationList = ref([]);
    async function searchForCertification(event){
      if(event.query == '')
        return;
      sleep(500);
      try {
        termToSearch.value = event.query;
        const response = await requestHandler.executeRequest('/api/job_certification/search', {term: termToSearch.value}, 'get');
        let values = [];
        let userLang = store.getters.getUserLang && ['en','pt','es'].includes(store.getters.getUserLang) ? store.getters.getUserLang : 'en';
        let responseAsArray = utils.parseToArray(response.data.data);
        for(let certification of responseAsArray){
            let val = certification[userLang] == null ? certification['en'] : certification[userLang];
            if(!utils.isInArray(certificationName.value, val, 'name')){
                values.push({
                    name: val, 
                    code: certification['certification_type']
                });
            }
        }
        certificationList.value = values;
        if(responseAsArray.length < 1){
            openConfirmationModal('certificationSearch');
        }else{
            termToSearch.value = '';
        }
      } catch (error) {
        console.error("Error fetching skills:", error);
      }
    }

    async function handleAutoCompleteElements(){
      let tries = 0;
      let autocompleteEls = document.querySelectorAll('.autocomplete-w-full');
      while(autocompleteEls.length < 1){
          autocompleteEls = document.querySelectorAll('.autocomplete-w-full');
          tries++;
          await sleep(50);
          if(tries == 30){
          break;
          }
      }
      if(autocompleteEls.length < 1)
          return;
      autocompleteEls.forEach(el => {
        if(el.style.width == '')
          el.style.setProperty('width', '100%', 'important');
          const ulEl = el.querySelector('ul');
          if(ulEl){
            ulEl.style.setProperty('width', '100%', 'important');
            // ulEl.style.setProperty('border-radius', '9999px', 'important');
            let inputEl = ulEl.querySelector('input');
          if(inputEl){
            inputEl.style.setProperty('width', '100%', 'important');
            inputEl.style.setProperty('outline', 'unset', 'important');
          }
        }
      });
    }

    async function confirmationResponse(result){
        confirmationModalVisibility.value = false;
        if(result.confirmation){
            actionIsBeingExecuted.value = true;
            switch(currentConfirmationData.action){
                case 'certificationSearch':
                    addCustomCertification();
                break;
                case 'certificationSave':
                    await saveCertification();
                break;
            }
            actionIsBeingExecuted.value = false;
        }
        currentConfirmationData.action = '';
    }

    async function saveCertification(){
        let certificationsIds   = [];
        let certificationNames = [];
        for(let data of certificationName.value){
            certificationsIds.push(data['code']);
            certificationNames.push(data['name']);
            let id = `${data.code}-${data.name.replaceAll(' ', 'aaaa-aaaa-aaaa')}`;
            highlightDropDown(id, false);
        }
        const response = await requestHandler.executeRequest(`api/joblist/managecertification/${jobId.value}`, {
            action: 'sync', certifications_ids: certificationsIds, certifications_names: certificationNames
        }, 'post');
        if(!response.success){
            let errorMessage = certificationsIds.length > 1 ? translate('certifications not saved') : translate('certification not saved');
            showToast(toast, errorMessage, 'error');
            return;
        }
        let successMessage = certificationsIds.length > 1 ? translate('certifications saved') : translate('certification saved');
        showToast(toast, successMessage);
        await jobData.updateMyCompanyJobs(jobId.value, response.data.data);
        store.dispatch('saveOnDataChest', {
            key: `save_job_${jobId.value}`,
            value: response.data.data
        });
    }

    function highlightDropDown(id = '', mark = true){
        id = id.replaceAll(' ', 'aaaa-aaaa-aaaa');
        let el = document.querySelector(`.certification-opt-${id}`);
        if(el != undefined && el.querySelector('.dropdown-el') != null){
            el.querySelector('.dropdown-el').style.border = mark ? '1px solid red' : '';
        }
    }

    let confirmationModalOpen = ref(false);
    function addCustomCertification(){
        confirmationModalVisibility.value = false;
        if(confirmationModalOpen.value){
            confirmationModalOpen.value = false;
            return;
        }
        customCertifications.value.push({code: 0, name: termToSearch.value});
        certificationName.value.push({code: 0, name: termToSearch.value})
        termToSearch.value = ''; 
        document.querySelector('#certificationAutocomplete').value = '' ;
    }

    // function handleInput(event){
    //   const newItem = event[event.length - 1];
    //   if(newItem == undefined)
    //     return;
    //   if (!certificationName.value.some(item => item.name === newItem.name)) {
    //     certificationName.value = [...certificationName.value, newItem];
    //   } else {
    //     event.pop();
    //   }
    // }
</script>